/**
 * ENTITY_SEARCH `prefill_map`: `{ siblingCodeName: "dotted.path" }`. On select,
 * each path is resolved against the picked option's raw `data` (e.g.
 * `values.address` on a directory record, `attribute_summary.email` on a
 * partnership) and written to the sibling field, which stays editable.
 */

/** Resolve a dotted path into a nested record, or `""` when any segment is
 *  missing, so a prefill never writes `undefined` into a form field. */
export function resolvePath(source: Record<string, unknown>, path: string): unknown {
  let cur: unknown = source
  for (const segment of path.split('.')) {
    if (cur && typeof cur === 'object' && segment in (cur as object)) {
      cur = (cur as Record<string, unknown>)[segment]
    } else {
      return ''
    }
  }
  return cur ?? ''
}

/**
 * Whether a stored value holds nothing, so a fill may write it. A picked
 * record (`{uuid, name}`) is blank without a name; any other object (an
 * address) only when none of its parts holds text.
 */
export function isBlankValue(value: unknown): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (typeof value === 'object' && !Array.isArray(value)) {
    const record = value as Record<string, unknown>
    if ('name' in record) return isBlankValue(record.name)
    return Object.values(record).every(isBlankValue)
  }
  return false
}

/**
 * A stored search-field value as the picker's `{uuid, name}`: a picked record
 * as it is, a name typed before the field was a search as an unlisted pick
 * (`uuid: ""`), and anything without a name as `null`.
 */
export function asSearchPick(value: unknown): { uuid: string; name: string } | null {
  if (typeof value === 'string') {
    return value.trim() ? { uuid: '', name: value.trim() } : null
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const record = value as Record<string, unknown>
    const name = typeof record.name === 'string' ? record.name.trim() : ''
    if (!name) return null
    return { uuid: typeof record.uuid === 'string' ? record.uuid : '', name }
  }
  return null
}

/** The sibling values a pick fills, keyed by code_name. */
export function resolvePrefill(
  data: Record<string, unknown>,
  prefillMap: Record<string, string>,
): Record<string, unknown> {
  const resolved: Record<string, unknown> = {}
  for (const [codeName, path] of Object.entries(prefillMap)) {
    resolved[codeName] = resolvePath(data, path)
  }
  return resolved
}

/** A blank of the same kind as `value`: an object keeps its keys, emptied. */
function blankLike(value: unknown): unknown {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? Object.fromEntries(Object.keys(value).map((key) => [key, '']))
    : ''
}

/**
 * What a pick writes into the fields its `prefill_map` fills. The picked
 * record's values go in (`resolved` is `{}` for a typed name or a cleared
 * pick); a field it leaves empty is blanked when it still holds what the
 * previous supplier filled (`previous`), so nothing of that supplier lingers,
 * while a value edited since is kept. `filled` is what this pick wrote.
 */
export function prefillWrites(
  resolved: Record<string, unknown>,
  previous: Record<string, unknown>,
  current: Record<string, unknown>,
): { writes: Record<string, unknown>; filled: Record<string, unknown> } {
  const filled = Object.fromEntries(
    Object.entries(resolved).filter(([, value]) => value !== ''),
  )
  const writes: Record<string, unknown> = { ...filled }
  for (const [codeName, value] of Object.entries(previous)) {
    // The form stores a copy of what was written, so compare contents.
    if (!(codeName in filled) && JSON.stringify(current[codeName]) === JSON.stringify(value)) {
      writes[codeName] = blankLike(value)
    }
  }
  return { writes, filled }
}
